import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { fetchLegalPage } from '../api';
import '../styles/pages/Legal.scss';

/**
 * Renders the lightweight body markup stored by the backend legal pages:
 * "## " section headings, "- " bullet lines and blank-line separated
 * paragraphs. The same contract is edited from the console's web
 * management tab.
 */
function renderBody(body) {
    return (body || '')
        .split(/\n{2,}/)
        .map((block) => block.trim())
        .filter(Boolean)
        .map((block, i) => {
            if (block.startsWith('## ')) {
                return <h2 key={i}>{block.slice(3)}</h2>;
            }
            const lines = block.split('\n');
            if (lines.every((line) => line.startsWith('- '))) {
                return (
                    <ul key={i}>
                        {lines.map((line, j) => (
                            <li key={j}>{line.slice(2)}</li>
                        ))}
                    </ul>
                );
            }
            return <p key={i}>{block}</p>;
        });
}

const Legal = ({ kind }) => {
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    usePageTitle(page ? page.title : kind === 'terms' ? 'Terms of Service' : 'Privacy Policy');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        fetchLegalPage(kind)
            .then((data) => {
                if (!cancelled) {
                    setPage(data);
                    setLoading(false);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err.message);
                    setLoading(false);
                }
            });
        return () => { cancelled = true; };
    }, [kind]);

    if (loading) {
        return (
            <>
                <Navbar />
                <div className="page-legal">
                    <div className="container text-center py-5">
                        <p className="text-muted">Loading...</p>
                    </div>
                </div>
            </>
        );
    }

    if (error || !page) {
        return (
            <>
                <Navbar />
                <div className="page-legal">
                    <div className="container text-center py-5">
                        <p className="text-danger">Unable to load content. Please try again later.</p>
                    </div>
                </div>
            </>
        );
    }

    const updated = new Date(page.updated_at).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });

    return (
        <>
            <Navbar />
            <div className="page-legal">
                <div className="container">
                    <div className="legal-header">
                        <p className="subtitle">Evib Group</p>
                        <h1>{page.title}</h1>
                        <p className="legal-updated">Last updated {updated}</p>
                    </div>
                    <div className="legal-body">{renderBody(page.body)}</div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default Legal;
